<template>
  <div class="purchase-cargo-insurance-additional-information">
    <Fieldset :legend="t('cargoInsurance.shipment')">
      <div class="grid">
        <CargoInsuranceEquipmentTypeWrappedSelect
          v-if="shipment"
          v-model="quoteRequest.equipmentType"
          :shipment="shipment"
        />
      </div>
    </Fieldset>

    <Fieldset :legend="t('cargoInsurance.cargo')">
      <div class="grid">
        <CargoInsuranceCommoditiesWrappedMultiSelect
          v-if="shipment?.id"
          v-model="quoteRequest.commodities"
          :shipment-id="shipment.id"
        />

        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="purchase-cargo-insurance-coverage-limit"
          :label="t('cargoInsurance.coverageLimit')"
          class="col"
          :help-text="t('cargoInsurance.coverageLimitHelpText')"
          :errors="v$.coverageLimit.amount.$errors"
        >
          <InputCurrency
            :id="inputId"
            v-model="quoteRequest.coverageLimit.amount"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :currency="quoteRequest.coverageLimit.currency"
            :min="0"
            :max="2000000"
            :placeholder="t('cargoInsurance.coverageLimit')"
          />
        </InputWrapper>
      </div>
    </Fieldset>

    <Fieldset :legend="t('cargoInsurance.insured.insured')">
      <div v-if="!contactsForInsured.loading" class="grid">
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="purchase-cargo-insurance-insured-name"
          :label="t('cargoInsurance.insured.companyName')"
          class="col-12"
          :errors="v$.insured?.companyName?.$errors"
        >
          <InputText
            :id="inputId"
            v-model="quoteRequest.insured.companyName"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :placeholder="t('cargoInsurance.insured.companyName')"
          />
        </InputWrapper>
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="purchase-cargo-insurance-insured-email"
          :label="t('cargoInsurance.insured.email')"
          class="col-12"
          :errors="v$.insured.email.$errors"
        >
          <InputText
            :id="inputId"
            v-model="quoteRequest.insured.email"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :placeholder="t('cargoInsurance.insured.email')"
          />
        </InputWrapper>
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="purchase-cargo-insurance-insured-address-address1"
          :label="t('address.addressLineNum', { num: 1 })"
          class="col-12"
          :errors="v$.insured.address.addressLine1.$errors"
        >
          <InputText
            :id="inputId"
            v-model="quoteRequest.insured!.address!.addressLine1"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :placeholder="t('address.addressLineNum', { num: 1 })"
          />
        </InputWrapper>
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="purchase-cargo-insurance-insured-address-city"
          :label="t('address.city')"
          class="col-3"
          :errors="v$.insured.address.locality.$errors"
        >
          <InputText
            :id="inputId"
            v-model="quoteRequest.insured!.address!.locality"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :placeholder="t('address.city')"
          />
        </InputWrapper>
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="purchase-cargo-insurance-insured-address-state"
          :label="t('address.state')"
          class="col-3"
          :errors="v$.insured.address.region.$errors"
        >
          <InputRegion
            :id="inputId"
            v-model="quoteRequest.insured!.address!.region"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :country="quoteRequest.insured!.address!.country"
            append-to="body"
          />
        </InputWrapper>
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          :help-text="insuredPostalCodeInfo?.helpText"
          input-id="purchase-cargo-insurance-insured-address-postal"
          :label="t('address.postalCode')"
          class="col-3"
          :errors="v$.insured.address.postalCode.$errors"
        >
          <InputText
            :id="inputId"
            v-model="quoteRequest.insured!.address!.postalCode"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :placeholder="t('address.postalCode')"
          />
        </InputWrapper>
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="purchase-cargo-insurance-insured-address-country"
          :label="t('address.country')"
          class="col-3"
          :errors="v$.insured.address.country.$errors"
        >
          <CountryDropdown
            :id="inputId"
            v-model="quoteRequest.insured!.address!.country"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            append-to="body"
          />
        </InputWrapper>
      </div>
      <PurchaseCargoInsuranceLoadingSkeleton
        v-if="contactsForInsured.loading"
      />
    </Fieldset>
  </div>
</template>

<script setup lang="ts">
import { email } from '@vuelidate/validators';
import axios from 'axios';
import { computed, Ref, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  maxValue,
  minValue,
  required,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { Loadable, POSTAL_CODE_INFOS } from '@rygen/rygen-ui/model';

import api from '@/api';
import { CargoInsuranceQuoteRequest, Contact, Shipment } from '@/model';

import CargoInsuranceCommoditiesWrappedMultiSelect from '@/components/cargoInsurance/CargoInsuranceCommoditiesWrappedMultiSelect.vue';
import CargoInsuranceEquipmentTypeWrappedSelect from '@/components/cargoInsurance/CargoInsuranceEquipmentTypeWrappedSelect.vue';
import PurchaseCargoInsuranceLoadingSkeleton from '@/components/cargoInsurance/PurchaseCargoInsuranceLoadingSkeleton.vue';
import InputCurrency from '@/components/form/InputCurrency.vue';
import CountryDropdown from '@rygen/rygen-ui/components/form/CountryDropdown.vue';
import InputRegion from '@rygen/rygen-ui/components/form/InputRegion.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import Fieldset from 'primevue/fieldset';
import InputText from 'primevue/inputtext';

const props = defineProps<{
  shipment: Shipment;
}>();

const { shipment } = toRefs(props);

const quoteRequest = defineModel('quoteRequest', {
  type: CargoInsuranceQuoteRequest,
  default: () => new CargoInsuranceQuoteRequest(),
});

const { t } = useI18n();

const contactsForInsured: Ref<Loadable<Contact[]>> = ref({
  loading: false,
  data: [],
});

const insuredPostalCodeInfo = computed(() => {
  return POSTAL_CODE_INFOS.find(
    (p) => p.countryCode === quoteRequest.value?.insured?.address?.country,
  );
});

const rules = {
  coverageLimit: {
    amount: {
      required: required(t('cargoInsurance.coverageLimit')),
      minValue: minValue(0.01, t('cargoInsurance.coverageLimit')),
      maxValue: maxValue(2000000, t('cargoInsurance.coverageLimit')),
    },
  },
  equipmentType: {},
  commodities: {},
  insured: {
    companyName: {
      required: required(t('cargoInsurance.insured.companyName')),
    },
    email: {
      required: required(t('cargoInsurance.insured.email')),
      email: email,
    },
    address: {
      addressLine1: {
        required: required(t('address.addressLineNum', { num: 1 })),
      },
      locality: {
        required: required(t('address.city')),
      },
      region: {
        required: required(t('address.region')),
      },
      postalCode: {
        required: required(t('address.postalCode')),
      },
      country: {
        required: required(t('address.country')),
      },
    },
  },
};

const { v$ } = useValidations(quoteRequest, rules);

watch(
  () => shipment.value.id,
  async (shipmentId) => {
    if (shipmentId) {
      await Promise.allSettled([fetchContactsForInsurance(shipmentId)]);
      v$.value.$reset();
    }
  },
  { immediate: true },
);

watch(
  () => contactsForInsured.value.data,
  () => {
    if (contactsForInsured.value.data?.length) {
      quoteRequest.value.insured = new Contact(
        contactsForInsured.value.data[0],
      );
      v$.value.$reset();
    }
  },
);

async function fetchContactsForInsurance(shipmentId: number) {
  contactsForInsured.value.data = [];
  try {
    contactsForInsured.value.loading = true;
    contactsForInsured.value.data =
      await api.shipmentCargoInsurance.getContactsForInsurance(shipmentId);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    contactsForInsured.value.error = e as Error;
    console.error('Error loading contacts for insurance', e);
  } finally {
    contactsForInsured.value.loading = false;
  }
}
</script>

<style>
.purchase-cargo-insurance-additional-information {
  .p-fieldset {
    /* Prevent MultiSelect values from growing too large */
    min-width: 0;
  }
}
</style>
